import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useStore } from '../../../store'
import { useToast } from '@/components/Toast';
import type { Dayjs } from 'dayjs';
import { Table, Drawer, Input } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Pagination } from "@/components/Pagination";
import GenericQueryCard, { QueryField } from '@/components/GenericQueryCard';
import { MobileTableCard } from '@/components/MobileCards';
import type { DailySummary } from '@/types/financialDetails';
import { fetchDailyList, exportData } from '@/services/financialDetails';
import dayjs from 'dayjs';

export const routeConfig: RouteConfig = {
    title: 'route.financialDetails.daily',
    layout: true,
    sort: 2
};

const DailySummary = () => {
    const { t } = useLocale();
    const { UserStore } = useStore();
    const toast = useToast();

    const [timeRange, setTimeRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(1689081600);
    const [dailySummaryData, setDailySummaryData] = useState<DailySummary[]>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);

    const pageType = 3; // 1 - commission, 2 - transaction, 3 - summary

    const buildDailyParams = (time_range: [Dayjs | null, Dayjs | null]) => {
        let params: any = {};
        if (time_range[0] && time_range[1]) {
            params.time_range = [time_range[0].unix(), time_range[1].unix()];
        }
        params.agent_id = UserStore.userInfo?.id;
        params.invite_code = UserStore.userInfo?.inviteCode;
        return params;
    };

    const handleReset = async () => {
        try {
            setTimeRange([null, null]);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            // fetch new data based on the reset filters
            const res = await fetchDailyList({}, 1, pagination.pageSize);
            setDailySummaryData(res.list);
            setTotal(res.total);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('financialDetails.daily.fetchError') || 'An error occurred while resetting.'
            });
        }
    };

    const handleQuery = async () => {
        try {
            const params = buildDailyParams(timeRange);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            const res = await fetchDailyList(params, pagination.current, pagination.pageSize);
            setDailySummaryData(res.list);
            setTotal(res.total);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('financialDetails.daily.fetchError') || 'An error occurred while querying.'
            });
        }
    };

    function exportToCsv(filename: string, rows: any[]) {
        if (!rows.length) return;
        const keys = Object.keys(rows[0]);
        const csvContent =
            keys.join(',') + '\n' +
            rows.map(row => keys.map(k => `"${row[k] ?? ''}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const handleExport = async () => {
        try {
            console.log('Exporting data...');
            setLoading(true);
            const params = buildDailyParams(timeRange);
            const result = await exportData(params, pageType);
            
            // Export the data list to CSV
            if (result && result.list && result.list.length > 0) {
                exportToCsv('daily_summary_data.csv', result.list);
                toast.success({
                    message: t('financialDetails.daily.exportSuccess') || 'Data exported successfully!'
                });
            } else {
                toast.warning({
                    message: t('financialDetails.daily.noDataToExport') || 'No data to export.'
                });
            }
        } catch (error: any) {
            console.error('Export failed:', error);
            toast.error({
                message: error?.message || t('financialDetails.daily.exportError') || 'Export failed. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    }

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('invitationPromotion.invitationLink.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };

    const formatNumber = (num: number | string | null | undefined) => {
        if (num === null || num === undefined) return '--';
        if (isNaN(Number(num))) return '--';
        // 1000000.023024 = 1,000,000.02
        return Number(num).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    };

    const queryFields: QueryField[] = [
        {
            type: 'dateRange',
            name: 'timeRange',
            label: t('financialDetails.daily.timeRange'),
            value: timeRange,
            onChange: (dates: [Dayjs | null, Dayjs | null] | null) => {
                setTimeRange(dates ?? [null, null]);
            },
            placeholder: [t('financialDetails.daily.startTime'), t('financialDetails.daily.endTime')],
        }
    ];

    const dailySummaryColumns: ColumnsType<DailySummary> = [
        {
            title: t('financialDetails.daily.table.date'),
            dataIndex: 'date',
            key: 'date',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text) => formatDate(text)
        },
        {
            title: t('financialDetails.daily.table.totalCommission'),
            dataIndex: 'totalCommission',
            key: 'totalCommission',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => <span className="break-all whitespace-normal">{formatNumber(text)}</span>
        },
        {
            title: t('financialDetails.daily.table.totalTransactionAmount'),
            dataIndex: 'totalTransactionAmount',
            key: 'totalTransactionAmount',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => <span className="break-all whitespace-normal">{formatNumber(text)}</span>
        },
        {
            title: t('financialDetails.daily.table.netHandlingFee'),
            dataIndex: 'netHandlingFee',
            key: 'netHandlingFee',
            align: 'start',
            width: 100,
            ellipsis: true,
            render: (text) => <span className="break-all whitespace-normal">{formatNumber(text)}</span>
        },
        {
            title: t('financialDetails.daily.table.newInvitedPeopleCount'),
            dataIndex: 'newInvitedPeopleCount',
            key: 'newInvitedPeopleCount',
            align: 'start',
            width: 100,
            ellipsis: true,
        },
    ];

    const mobileDailySummaryColumns = dailySummaryColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // try {
                //     const groupRes = await fetchGroupID({});
                //     setCustomerGroups(groupRes);
                // } catch (groupError: any) {
                //     toast.error({
                //         message: groupError?.message || t('customerManagement.clientList.groupFetchError') || 'An error occurred while fetching group list.'
                //     });
                // }

                try {
                    const params = buildDailyParams(timeRange);
                    const res = await fetchDailyList(params, pagination.current, pagination.pageSize);
                    setDailySummaryData(res.list);
                    setTotal(res.total);
                } catch (error: any) {
                    toast.error({
                        message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching client list.'
                    });
                }
                setLastUpdated(Math.floor(Date.now() / 1000));
            } catch (error: any) {
                toast.error({
                    message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching data.'
                });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [pagination.current, pagination.pageSize]);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            <GenericQueryCard
                title={t('financialDetails.daily.title')}
                subtitle={t('financialDetails.daily.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
                fields={queryFields}
                onReset={handleReset}
                onQuery={handleQuery}
                onExport={handleExport}
                gridCols="grid-cols-2 md:grid-cols-4"
            />
            
            {/* Table Container with Custom Layout */}
            <div className="mt-6 bg-card-background dark:bg-card-background rounded-lg p-6 overflow-hidden">
                {/* Table Content */}
                <div className="hidden md:block h-[556px] overflow-hidden overflow-y-auto overflow-x-auto">
                    <Table
                        dataSource={dailySummaryData}
                        columns={dailySummaryColumns}
                        rowKey={(record, idx) => `${record.date}-${idx}`}
                        pagination={false}
                        loading={loading}
                        size="small"
                        // scroll={{ x: 850 }}
                        className="custom-table"
                        showHeader={true}
                        locale={{ emptyText: '' }}
                        components={{
                            body: {
                                wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                            }
                        }}
                    />
                </div>
                <div className="block md:hidden">
                    {dailySummaryData.map((row, idx) => (
                        <MobileTableCard
                            key={`${row.date}-${idx}`}
                            record={row}
                            columns={mobileDailySummaryColumns}
                            t={t}
                        />
                    ))}
                </div>
                <div className="mt-4 flex justify-end">
                    <Pagination
                        currentPage={pagination.current}
                        totalPages={Math.ceil(total / pagination.pageSize)}
                        onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                        pageSize={pagination.pageSize}
                        setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                        pageSizes={[10, 20, 50, 100]}
                        isPageSizeVisible={true}
                        disabled={loading}
                    />
                </div>
            </div>
        </div>
    );
};

export default DailySummary;